/* ==========================================================================
   NINNALI BABY — Landing page interactions
   Gallery, lightbox zoom, quantity/variant, AJAX cart drawer, sticky buy bar,
   FAQ accordion, carousels, CEP shipping lookup (real Shopify rates), reveal.
   ========================================================================== */
(() => {
  'use strict';

  const NN = window.NN || {};
  const routes = Object.assign(
    { root: '/', cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change' },
    NN.routes || {}
  );
  const root = routes.root.endsWith('/') ? routes.root : routes.root + '/';
  const html = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  html.classList.add('nn-js');

  /* ---------------- Helpers ---------------- */
  const escapeHtml = (str) =>
    String(str ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  function formatMoney(cents, format = NN.moneyFormat || 'R$ {{amount_with_comma_separator}}') {
    const value = Number(cents) || 0;
    const fmt = String(format).replace(/<[^>]*>/g, '');
    const withDelimiters = (amount, decimals, thousands = '.', decimal = ',') => {
      const fixed = (amount / 100).toFixed(decimals);
      const [int, dec] = fixed.split('.');
      const intDelimited = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec ? intDelimited + decimal + dec : intDelimited;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount': return withDelimiters(value, 2, ',', '.');
        case 'amount_no_decimals': return withDelimiters(value, 0, ',', '.');
        case 'amount_with_space_separator': return withDelimiters(value, 2, ' ', ',');
        case 'amount_with_period_and_space_separator': return withDelimiters(value, 2, ' ', '.');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        case 'amount_no_decimals_with_space_separator': return withDelimiters(value, 0, ' ', ',');
        default: return withDelimiters(value, 2, '.', ',');
      }
    });
  }

  const setPrice = (el, text) => {
    const hidden = el.querySelector('.nn-visually-hidden');
    let html = escapeHtml(text);
    if (el.hasAttribute('data-nn-price-split')) {
      const i = text.search(/\d/);
      if (i > 0) html = `<span class="nn-price__sym">${escapeHtml(text.slice(0, i).trim())}</span>${escapeHtml(text.slice(i))}`;
    }
    el.innerHTML = (hidden ? hidden.outerHTML + ' ' : '') + html;
  };

  async function fetchJSON(url, options = {}) {
    const res = await fetch(url, Object.assign({ headers: { Accept: 'application/json' } }, options));
    let data = null;
    try { data = await res.json(); } catch (e) { /* empty body */ }
    return { ok: res.ok, status: res.status, data };
  }

  /* ---------------- Header ---------------- */
  const header = $('[data-nn-header]');
  const promo = $('.nn-promo');
  if (promo && 'ResizeObserver' in window) {
    new ResizeObserver(() => html.style.setProperty('--nn-promo-h', promo.offsetHeight + 'px')).observe(promo);
  }
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 4);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------------- Lightbox (zoom) ---------------- */
  const lightbox = (() => {
    const el = $('[data-nn-lightbox]');
    if (!el || typeof el.showModal !== 'function') return null;
    const stage = $('[data-nn-lightbox-stage]', el);
    const img = $('[data-nn-lightbox-img]', el);
    let items = [];
    let index = 0;

    const unzoom = () => el.classList.remove('is-zoomed');
    const show = (i) => {
      index = (i + items.length) % items.length;
      unzoom();
      img.src = items[index].src;
      img.alt = items[index].alt || '';
    };
    const open = (list, i) => {
      items = list;
      el.classList.toggle('is-single', items.length < 2);
      show(i);
      el.showModal();
      html.classList.add('nn-locked');
    };
    const toggleZoom = (event) => {
      if (el.classList.contains('is-zoomed')) return unzoom();
      const rect = img.getBoundingClientRect();
      const rx = (event.clientX - rect.left) / rect.width;
      const ry = (event.clientY - rect.top) / rect.height;
      el.classList.add('is-zoomed');
      requestAnimationFrame(() => {
        stage.scrollLeft = rx * img.offsetWidth - stage.clientWidth / 2;
        stage.scrollTop = ry * img.offsetHeight - stage.clientHeight / 2;
      });
    };

    img.addEventListener('click', toggleZoom);
    stage.addEventListener('click', (e) => { if (e.target === stage && !el.classList.contains('is-zoomed')) el.close(); });
    $('[data-nn-lightbox-close]', el).addEventListener('click', () => el.close());
    $('[data-nn-lightbox-prev]', el).addEventListener('click', () => show(index - 1));
    $('[data-nn-lightbox-next]', el).addEventListener('click', () => show(index + 1));
    el.addEventListener('keydown', (e) => {
      if (el.classList.contains('is-zoomed') || items.length < 2) return;
      if (e.key === 'ArrowLeft') show(index - 1);
      if (e.key === 'ArrowRight') show(index + 1);
    });
    el.addEventListener('close', () => { unzoom(); html.classList.remove('nn-locked'); });

    let touchX = null;
    stage.addEventListener('touchstart', (e) => { touchX = e.touches.length === 1 ? e.touches[0].clientX : null; }, { passive: true });
    stage.addEventListener('touchend', (e) => {
      if (touchX === null || el.classList.contains('is-zoomed') || items.length < 2) return;
      const dx = e.changedTouches[0].clientX - touchX;
      if (Math.abs(dx) > 50) show(index + (dx < 0 ? 1 : -1));
      touchX = null;
    });

    return { open };
  })();

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-nn-zoom]');
    if (!trigger || !lightbox) return;
    e.preventDefault();
    const group = trigger.closest('[data-nn-gallery], section') || document;
    const triggers = $$('[data-nn-zoom]', group);
    lightbox.open(triggers.map((t) => ({ src: t.dataset.src, alt: t.dataset.alt })), Math.max(0, triggers.indexOf(trigger)));
  });

  /* ---------------- Gallery ---------------- */
  $$('[data-nn-gallery]').forEach((gallery) => {
    const track = $('[data-nn-track]', gallery);
    const slides = $$('[data-nn-slide]', gallery);
    const thumbs = $$('[data-nn-thumb]', gallery);
    const thumbsWrap = $('[data-nn-thumbs]', gallery);
    const prev = $('[data-nn-prev]', gallery);
    const next = $('[data-nn-next]', gallery);
    const current = $('[data-nn-current]', gallery);
    const dots = $$('[data-nn-dot]', gallery);
    if (!track || slides.length === 0) return;
    let active = 0;
    const step = () => (slides.length > 1 ? slides[1].offsetLeft - slides[0].offsetLeft : track.clientWidth) || track.clientWidth;

    // Miniaturas mostram as próximas fotos depois da principal (a atual fica oculta via CSS).
    const orderThumbs = (i) => {
      thumbs.forEach((t, n) => {
        t.setAttribute('aria-current', n === i ? 'true' : 'false');
        t.style.order = String((n - i + thumbs.length) % thumbs.length);
      });
      if (thumbsWrap) thumbsWrap.scrollLeft = 0;
    };
    orderThumbs(0);

    const setActive = (i) => {
      if (i === active) return;
      active = i;
      orderThumbs(i);
      if (current) current.textContent = String(i + 1);
      dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === slides.length - 1;
    };
    // No PC a troca é instantânea com um fade curto (sem deslizar pelas fotos do meio).
    // Durante a troca, o scroll não reordena as miniaturas — evita o "pisca" na fileira de baixo.
    const desktop = window.matchMedia('(min-width: 990px)');
    let locked = false;
    let unlockTimer;
    const go = (i) => {
      const target = Math.max(0, Math.min(slides.length - 1, i));
      if (target === active) return;
      const instant = reducedMotion || desktop.matches;
      locked = true;
      clearTimeout(unlockTimer);
      setActive(target);
      track.scrollTo({ left: target * step(), behavior: instant ? 'auto' : 'smooth' });
      if (instant && !reducedMotion) {
        const slide = slides[target];
        slide.classList.remove('is-entering');
        void slide.offsetWidth;
        slide.classList.add('is-entering');
      }
      unlockTimer = setTimeout(() => { locked = false; }, instant ? 80 : 700);
    };

    // Carrega todas as fotos da galeria logo após a página abrir, para a troca não ter atraso.
    const warm = () => $$('img[loading="lazy"]', track).forEach((img) => { img.loading = 'eager'; });
    if (document.readyState === 'complete') warm(); else window.addEventListener('load', warm, { once: true });

    let ticking = false;
    track.addEventListener('scroll', () => {
      if (locked || ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
        setActive(atEnd ? slides.length - 1 : Math.round(track.scrollLeft / step()));
        ticking = false;
      });
    }, { passive: true });

    prev && prev.addEventListener('click', () => go(active - 1));
    next && next.addEventListener('click', () => go(active + 1));
    thumbs.forEach((t) => t.addEventListener('click', () => go(Number(t.dataset.nnThumb))));
    gallery.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') go(active - 1);
      if (e.key === 'ArrowRight') go(active + 1);
    });
    active = -1;
    setActive(0);
  });

  /* ---------------- Video bubble (loop no canto da foto → vídeo grande com som) ---------------- */
  $$('[data-nn-bubble]').forEach((bubble) => {
    const dialog = bubble.parentElement.querySelector('[data-nn-bubble-dialog]');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    const big = $('video', dialog);
    const small = $('video', bubble);
    if (small && reducedMotion) small.pause();
    const close = () => { if (big) big.pause(); if (dialog.open) dialog.close(); };
    // Vídeo grande: só passa em loop, sem som, sem controles nem menu de download.
    [big, small].forEach((v) => {
      if (!v) return;
      v.muted = true;
      v.loop = true;
      v.controls = false;
      v.setAttribute('controlslist', 'nodownload nofullscreen noremoteplayback');
      v.disablePictureInPicture = true;
      v.addEventListener('contextmenu', (e) => e.preventDefault());
    });
    bubble.addEventListener('click', () => {
      dialog.showModal();
      if (big) {
        big.muted = true;
        big.autoplay = true;
        const start = () => big.play().catch(() => {});
        big.play().catch(() => big.addEventListener('canplay', start, { once: true }));
      }
    });
    $('[data-nn-bubble-close]', dialog).addEventListener('click', close);
    dialog.addEventListener('click', (e) => { if (e.target === dialog) close(); });
    dialog.addEventListener('close', () => { if (big) big.pause(); });
  });

  /* ---------------- Product state (variant + quantity) ---------------- */
  const productEl = $('[data-nn-product]');
  const variantsEl = $('[data-nn-variants]');
  const variants = variantsEl ? JSON.parse(variantsEl.textContent || '[]') : [];
  const state = { qty: 1 };

  const qtyInput = $('[data-nn-qty-input]');
  const syncQty = (value) => {
    const max = qtyInput ? Number(qtyInput.max) || 99 : 99;
    state.qty = Math.max(1, Math.min(max, parseInt(value, 10) || 1));
    if (qtyInput) qtyInput.value = state.qty;
    $$('[data-nn-qty-mirror]').forEach((i) => { i.value = state.qty; });
  };
  if (qtyInput) {
    $('[data-nn-qty-minus]').addEventListener('click', () => syncQty(state.qty - 1));
    $('[data-nn-qty-plus]').addEventListener('click', () => syncQty(state.qty + 1));
    qtyInput.addEventListener('change', () => syncQty(qtyInput.value));
  }

  const updateVariant = (id) => {
    const v = variants.find((x) => String(x.id) === String(id));
    if (!v) return;
    $$('[data-nn-variant-id]').forEach((i) => { i.value = v.id; });
    const hasDiscount = v.compare_at_price && v.compare_at_price > v.price;
    const off = hasDiscount ? Math.round(((v.compare_at_price - v.price) * 100) / v.compare_at_price) : 0;
    $$('[data-nn-price]').forEach((el) => setPrice(el, formatMoney(v.price)));
    $$('[data-nn-compare]').forEach((el) => {
      el.hidden = !hasDiscount;
      if (hasDiscount) setPrice(el, formatMoney(v.compare_at_price));
    });
    $$('[data-nn-offer-top]').forEach((el) => { el.hidden = !hasDiscount; });
    $$('[data-nn-stock]').forEach((el) => { el.hidden = !v.available; });
    $$('[data-nn-save]').forEach((el) => {
      el.hidden = !hasDiscount || !v.available;
      const value = $('[data-nn-save-value]', el);
      if (value && hasDiscount) value.textContent = formatMoney(v.compare_at_price - v.price);
    });
    $$('[data-nn-installments]').forEach((el) => {
      const n = Number(el.dataset.count) || 1;
      const value = $('[data-nn-installment]', el);
      if (value) value.textContent = formatMoney(Math.round(v.price / n));
    });
    $$('[data-nn-badge]').forEach((el) => { el.hidden = !hasDiscount; el.textContent = (el.dataset.format || '{n}% OFF').replace('{n}', off); });
    $$('[data-nn-add]').forEach((btn) => {
      btn.disabled = !v.available;
      if (btn.dataset.labelBase) btn.dataset.label = btn.dataset.labelBase + ' • ' + formatMoney(v.price);
      $('[data-nn-add-label]', btn).textContent = v.available ? btn.dataset.label : 'Esgotado';
    });
    if (productEl && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url.toString());
    }
  };
  const variantSelect = $('[data-nn-variant-select]');
  variantSelect && variantSelect.addEventListener('change', () => updateVariant(variantSelect.value));

  /* ---------------- Cart drawer ---------------- */
  const drawer = $('[data-nn-drawer]');
  const drawerItems = drawer && $('[data-nn-drawer-items]', drawer);

  const imageUrl = (url, width) => (url ? url + (url.includes('?') ? '&' : '?') + 'width=' + width : '');

  function renderCart(cart) {
    $$('[data-nn-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
    if (!drawer) return;
    $('[data-nn-drawer-empty]', drawer).hidden = cart.item_count > 0;
    $('[data-nn-drawer-foot]', drawer).hidden = cart.item_count === 0;
    $('[data-nn-drawer-subtotal]', drawer).textContent = formatMoney(cart.total_price);
    drawerItems.innerHTML = cart.items.map((item) => {
      const variant = item.variant_title && !/default title/i.test(item.variant_title) ? item.variant_title : '';
      return `
        <li class="nn-drawer__item" data-key="${escapeHtml(item.key)}">
          ${item.image ? `<img src="${escapeHtml(imageUrl(item.image, 200))}" alt="" width="76" height="95" loading="lazy">` : '<span></span>'}
          <div>
            <p class="nn-drawer__item-title">${escapeHtml(item.product_title)}</p>
            ${variant ? `<p class="nn-drawer__item-variant">${escapeHtml(variant)}</p>` : ''}
            <p class="nn-drawer__item-price">${escapeHtml(formatMoney(item.final_line_price))}</p>
            <div class="nn-drawer__item-actions">
              <div class="nn-qty">
                <button type="button" class="nn-qty__btn" data-nn-line="${item.quantity - 1}" aria-label="Diminuir quantidade de ${escapeHtml(item.product_title)}">
                  <svg class="nn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M5 12h14"/></svg>
                </button>
                <span class="nn-qty__input" aria-label="Quantidade">${item.quantity}</span>
                <button type="button" class="nn-qty__btn" data-nn-line="${item.quantity + 1}" aria-label="Aumentar quantidade de ${escapeHtml(item.product_title)}">
                  <svg class="nn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
                </button>
              </div>
              <button type="button" class="nn-drawer__remove" data-nn-line="0" aria-label="Remover ${escapeHtml(item.product_title)}">
                <svg class="nn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 7h14M10 7V5h4v2M7 7l.8 12a1.5 1.5 0 0 0 1.5 1.4h5.4a1.5 1.5 0 0 0 1.5-1.4L17 7"/></svg>
              </button>
            </div>
          </div>
        </li>`;
    }).join('');
  }

  async function refreshCart() {
    const { ok, data } = await fetchJSON(routes.cart + '.js');
    if (ok && data) renderCart(data);
    return data;
  }

  function openDrawer() {
    if (!drawer || typeof drawer.showModal !== 'function') {
      window.location.href = routes.cart;
      return;
    }
    if (!drawer.open) drawer.showModal();
    html.classList.add('nn-locked');
  }

  if (drawer) {
    drawer.addEventListener('close', () => html.classList.remove('nn-locked'));
    drawer.addEventListener('click', (e) => {
      if (e.target === drawer || e.target.closest('[data-nn-drawer-close]')) drawer.close();
    });
    drawer.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-nn-line]');
      if (!btn) return;
      const key = btn.closest('[data-key]').dataset.key;
      drawer.classList.add('is-busy');
      const { ok, data } = await fetchJSON(routes.cartChange + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: Number(btn.dataset.nnLine) }),
      });
      drawer.classList.remove('is-busy');
      if (ok && data) renderCart(data);
      else await refreshCart();
    });
  }

  $$('[data-nn-cart-open]').forEach((link) => link.addEventListener('click', async (e) => {
    if (!drawer || typeof drawer.showModal !== 'function') return;
    e.preventDefault();
    openDrawer();
    await refreshCart();
  }));

  window.addEventListener('pageshow', (e) => { if (e.persisted) refreshCart(); });

  /* ---------------- Add to cart ---------------- */
  async function addToCart(form) {
    const btn = $('[data-nn-add]', form);
    const status = $('[data-nn-status]', form);
    const label = btn && $('[data-nn-add-label]', btn);
    if (!btn || btn.disabled) return;

    const id = Number($('[name="id"]', form).value);
    const qtyField = $('[name="quantity"]', form);
    const quantity = Math.max(1, parseInt(qtyField ? qtyField.value : state.qty, 10) || 1);

    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    if (status) { status.textContent = ''; status.classList.remove('is-error'); }

    // "Comprar agora" direto para o checkout: link de carrinho da Shopify com só este item.
    if (html.hasAttribute('data-nn-direct-checkout')) {
      window.location.href = root + 'cart/' + id + ':' + quantity;
      return;
    }

    try {
      const { ok, data } = await fetchJSON(routes.cartAdd + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity }] }),
      });
      if (!ok) {
        const msg = (data && (data.description || data.message)) || 'Não foi possível adicionar ao carrinho.';
        throw new Error(msg);
      }
      btn.classList.add('is-added');
      label.textContent = 'Adicionado ✓';
      if (status) status.textContent = 'Produto adicionado ao carrinho.';
      setTimeout(() => {
        btn.classList.remove('is-added');
        label.textContent = btn.dataset.label;
        if (status) status.textContent = '';
      }, 1800);
      const cart = await refreshCart();
      $$('[data-nn-cart-count]').forEach((el) => {
        el.classList.remove('is-bump'); void el.offsetWidth; el.classList.add('is-bump');
      });
      if (cart) openDrawer();
    } catch (err) {
      if (status) {
        status.textContent = err && err.message && !/fetch/i.test(err.message)
          ? err.message
          : 'Não foi possível adicionar. Verifique sua conexão e tente novamente.';
        status.classList.add('is-error');
      }
    } finally {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
    }
  }

  $$('form[data-nn-form]').forEach((form) => form.addEventListener('submit', (e) => {
    e.preventDefault();
    addToCart(form);
  }));

  /* ---------------- Sticky buy bar ---------------- */
  const sticky = $('[data-nn-sticky]');
  const mainBtn = $('[data-nn-form="main"] [data-nn-add]');
  const finalSection = $('[data-nn-final]');
  if (sticky && 'IntersectionObserver' in window) {
    const seen = { pastHero: false, finalVisible: false };
    const update = () => {
      const show = seen.pastHero && !seen.finalVisible;
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      sticky.inert = !show;
      html.classList.toggle('nn-sticky-on', show);
    };
    if (mainBtn) {
      // Scroll check instead of an observer: jumping from the footer back to the top
      // never crosses the button, which left the bar stuck over the price.
      let ticking = false;
      const check = () => {
        ticking = false;
        const past = mainBtn.getBoundingClientRect().bottom < 0;
        if (past !== seen.pastHero) { seen.pastHero = past; update(); }
      };
      window.addEventListener('scroll', () => {
        if (!ticking) { ticking = true; requestAnimationFrame(check); }
      }, { passive: true });
      check();
    } else {
      seen.pastHero = true;
    }
    if (finalSection) {
      new IntersectionObserver(([entry]) => { seen.finalVisible = entry.isIntersecting; update(); }, { threshold: 0.2 })
        .observe(finalSection);
    }
    update();
  }

  /* ---------------- FAQ accordion ---------------- */
  $$('[data-nn-accordion]').forEach((details) => {
    const summary = $('summary', details);
    const content = $('.nn-faq__a', details);
    if (!summary || !content) return;
    let anim = null;
    summary.addEventListener('click', (e) => {
      if (reducedMotion || !content.animate) return;
      e.preventDefault();
      if (anim) anim.cancel();
      if (!details.open) {
        details.open = true;
        const h = content.scrollHeight;
        anim = content.animate([{ height: '0px', opacity: 0 }, { height: h + 'px', opacity: 1 }], { duration: 280, easing: 'cubic-bezier(.2,.7,.2,1)' });
        anim.onfinish = () => { anim = null; };
      } else {
        const h = content.offsetHeight;
        anim = content.animate([{ height: h + 'px', opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 220, easing: 'ease-out' });
        anim.onfinish = () => { details.open = false; anim = null; };
      }
    });
  });

  /* ---------------- Carousels (features dots, reviews arrows) ---------------- */
  $$('[data-nn-carousel]').forEach((track) => {
    const section = track.closest('section');
    const dots = section ? $$('[data-nn-dots] .nn-dots__dot', section) : [];
    const items = Array.from(track.children);
    const step = () => (items[1] ? items[1].offsetLeft - items[0].offsetLeft : track.clientWidth);
    if (dots.length) {
      track.addEventListener('scroll', () => {
        const i = Math.round(track.scrollLeft / step());
        dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
      }, { passive: true });
    }
    const wrap = track.parentElement;
    const prev = wrap && $('[data-nn-carousel-prev]', wrap);
    const next = wrap && $('[data-nn-carousel-next]', wrap);
    const behavior = reducedMotion ? 'auto' : 'smooth';
    prev && prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior }));
    next && next.addEventListener('click', () => track.scrollBy({ left: step(), behavior }));
  });

  /* ---------------- CEP lookup (real Shopify shipping rates) ---------------- */
  const UF_RANGES = [
    [1000, 19999, 'SP'], [20000, 28999, 'RJ'], [29000, 29999, 'ES'], [30000, 39999, 'MG'], [40000, 48999, 'BA'],
    [49000, 49999, 'SE'], [50000, 56999, 'PE'], [57000, 57999, 'AL'], [58000, 58999, 'PB'], [59000, 59999, 'RN'],
    [60000, 63999, 'CE'], [64000, 64999, 'PI'], [65000, 65999, 'MA'], [66000, 68899, 'PA'], [68900, 68999, 'AP'],
    [69000, 69299, 'AM'], [69300, 69399, 'RR'], [69400, 69899, 'AM'], [69900, 69999, 'AC'], [70000, 72799, 'DF'],
    [72800, 72999, 'GO'], [73000, 73699, 'DF'], [73700, 76799, 'GO'], [76800, 76999, 'RO'], [77000, 77999, 'TO'],
    [78000, 78899, 'MT'], [79000, 79999, 'MS'], [80000, 87999, 'PR'], [88000, 89999, 'SC'], [90000, 99999, 'RS'],
  ];
  const ufFromCep = (digits) => {
    const n = parseInt(digits.slice(0, 5), 10);
    const hit = UF_RANGES.find(([a, b]) => n >= a && n <= b);
    return hit ? hit[2] : '';
  };
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  $$('[data-nn-cep]').forEach((form) => {
    const input = $('[data-nn-cep-input]', form);
    const out = $('[data-nn-cep-result]', form);
    const btn = $('button[type="submit"]', form);
    const say = (msg, isError) => { out.innerHTML = `<p class="${isError ? 'is-error' : ''}">${escapeHtml(msg)}</p>`; };

    input.addEventListener('input', () => {
      const d = input.value.replace(/\D/g, '').slice(0, 8);
      input.value = d.length > 5 ? d.slice(0, 5) + '-' + d.slice(5) : d;
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const digits = input.value.replace(/\D/g, '');
      if (digits.length !== 8) return say('Digite um CEP válido, com 8 números.', true);

      btn.disabled = true;
      say('Consultando…');
      try {
        const cart = await refreshCart();
        if (!cart || cart.item_count === 0) {
          say('Adicione o produto ao carrinho para ver as opções e os prazos de entrega para o seu CEP.');
          return;
        }
        const params = new URLSearchParams({
          'shipping_address[zip]': digits.slice(0, 5) + '-' + digits.slice(5),
          'shipping_address[country]': 'Brazil',
          'shipping_address[province]': ufFromCep(digits),
        }).toString();
        const prep = await fetchJSON(root + 'cart/prepare_shipping_rates.json?' + params, { method: 'POST' });
        if (!prep.ok && prep.status !== 202) throw new Error('prepare');

        let rates = null;
        for (let i = 0; i < 12 && rates === null; i++) {
          const res = await fetchJSON(root + 'cart/async_shipping_rates.json?' + params);
          if (res.ok && res.data && res.data.shipping_rates) rates = res.data.shipping_rates;
          else if (!res.ok && res.status !== 202) throw new Error('rates');
          else await wait(600);
        }
        if (!rates || rates.length === 0) return say('Não encontramos opções de entrega para este CEP.', true);

        out.innerHTML = '<ul>' + rates.map((r) => {
          const days = Array.isArray(r.delivery_days) && r.delivery_days.length
            ? r.delivery_days[r.delivery_days.length - 1] : null;
          const price = Number(r.price) === 0 ? 'Grátis' : formatMoney(Math.round(parseFloat(r.price) * 100));
          const eta = days ? ` · até ${days} ${days > 1 ? 'dias úteis' : 'dia útil'}` : '';
          return `<li><span>${escapeHtml(r.name)}${escapeHtml(eta)}</span><strong>${escapeHtml(price)}</strong></li>`;
        }).join('') + '</ul>';
      } catch (err) {
        say('Não foi possível consultar agora. O frete também é calculado na finalização da compra.', true);
      } finally {
        btn.disabled = false;
      }
    });
  });

  /* ---------------- Reveal on scroll ---------------- */
  const revealEls = $$('[data-nn-reveal]');
  if (!reducedMotion && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-in'); io.unobserve(entry.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    revealEls.forEach((el) => io.observe(el));
  } else {
    revealEls.forEach((el) => el.classList.add('is-in'));
  }

  /* ---------------- In-page links ---------------- */
  // Reviews anchor falls back to the Judge.me widget; header/footer links to sections
  // removed in the editor (e.g. the FAQ) are hidden instead of doing nothing.
  if (!document.getElementById('nn-avaliacoes')) {
    const jdgm = $('.jdgm-widget, .jdgm-review-widget');
    const host = jdgm && (jdgm.closest('.shopify-section') || jdgm);
    if (host) host.id = 'nn-avaliacoes';
  }
  $$('.nn-menu a[href^="#nn-"], .nn-foot a[href^="#nn-"]').forEach((a) => {
    if (document.getElementById(a.getAttribute('href').slice(1))) return;
    (a.closest('li') || a).setAttribute('data-nn-dead', '');
  });

  // Voltar do checkout pelo botão "voltar" do navegador não deixa o botão girando.
  window.addEventListener('pageshow', () => {
    $$('[data-nn-add].is-loading').forEach((b) => { b.classList.remove('is-loading'); b.removeAttribute('aria-busy'); });
  });
})();
